import { getFileObjectsFromSecLoanAttachments } from '@sec-loans-ui/utils/getFileObjectsFromSecLoanAttachments'
import {
  AdditionalInfoData,
  OtherOccupation,
  SecLoanAttachment,
  SecLoanOccupation,
  SecLoansCompanyForm,
  SecLoansFileCategory,
} from '@shared/sec-loans'
import { Occupation } from '@shared/types'
import * as fileUtils from '@ui-common/components/FormUpload/fileUtils'
import { convertUndefinedToNull } from '@ui-common/utils/form'
import { resolveFormEmptyString, resolveMapValue } from '@ui-common/utils/helpers'

import { getValidatedData } from '../../../../utils/helpers'
import { AdditionalInfoStepData, ApplicantStepDataValidator } from '../../../types'
import { isFormDataValid } from '../../../wizardData'
import { createFormValidator } from './formValidator'
import { AdditionalInfoFormData } from './types'

export const getAndValidateAdditionalInfoTypeData: ApplicantStepDataValidator<AdditionalInfoData> = ({
  validationData,
}) => {
  const additionalInfoData = validationData.applicantInfo?.additionalInfoData

  const isValid = isFormDataValid(additionalInfoData, createFormValidator)
  return getValidatedData(additionalInfoData, isValid)
}

export const convertToFormValues = (
  additionalInfoStepData: AdditionalInfoStepData | undefined,
  attachments?: SecLoanAttachment[],
): AdditionalInfoFormData => {
  const data = {
    additionalInfo: additionalInfoStepData?.additionalInfo,
    firstApplicant: {
      paySlipFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.PaySlip,
      ),
      financialStatementFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.FinancialStatement,
      ),
      taxDecisionBreakdownFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.TaxDecisionBreakdown,
      ),
      businessTaxReturnFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.BusinessTaxReturn,
      ),
      taxDecisionFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.TaxDecision,
      ),
      pensionCertificateFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.firstApplicantFileIds,
        attachments,
        SecLoansFileCategory.PensionCertificate,
      ),
    },
    secondApplicant: {
      paySlipFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.PaySlip,
      ),
      financialStatementFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.FinancialStatement,
      ),
      taxDecisionBreakdownFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.TaxDecisionBreakdown,
      ),
      businessTaxReturnFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.BusinessTaxReturn,
      ),
      taxDecisionFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.TaxDecision,
      ),
      pensionCertificateFiles: getFileObjectsFromSecLoanAttachments(
        additionalInfoStepData?.secondApplicantFileIds,
        attachments,
        SecLoansFileCategory.PensionCertificate,
      ),
    },
  } satisfies AdditionalInfoFormData
  return convertUndefinedToNull(data)
}

export const convertToSubmitData = (formData: AdditionalInfoFormData): AdditionalInfoStepData => {
  const { firstApplicant, secondApplicant } = formData

  return {
    additionalInfo: resolveFormEmptyString(formData?.additionalInfo),
    firstApplicantFileIds: fileUtils.extractFileIds([
      ...(firstApplicant?.paySlipFiles ?? []),
      ...(firstApplicant?.financialStatementFiles ?? []),
      ...(firstApplicant?.taxDecisionBreakdownFiles ?? []),
      ...(firstApplicant?.businessTaxReturnFiles ?? []),
      ...(firstApplicant?.taxDecisionFiles ?? []),
      ...(firstApplicant?.pensionCertificateFiles ?? []),
    ]),
    secondApplicantFileIds: fileUtils.extractFileIds([
      ...(secondApplicant?.paySlipFiles ?? []),
      ...(secondApplicant?.financialStatementFiles ?? []),
      ...(secondApplicant?.taxDecisionBreakdownFiles ?? []),
      ...(secondApplicant?.businessTaxReturnFiles ?? []),
      ...(secondApplicant?.taxDecisionFiles ?? []),
      ...(secondApplicant?.pensionCertificateFiles ?? []),
    ]),
  }
}

const resolveEntrepreneurCategoryMap = (companyForm: SecLoansCompanyForm | undefined) => {
  const companyFormMap: Record<SecLoansCompanyForm, SecLoansFileCategory[]> = {
    [SecLoansCompanyForm.LimitedCompany]: [
      SecLoansFileCategory.FinancialStatement,
      SecLoansFileCategory.PaySlip,
      SecLoansFileCategory.TaxDecisionBreakdown,
    ],
    [SecLoansCompanyForm.LimitedPartnership]: [
      SecLoansFileCategory.FinancialStatement,
      SecLoansFileCategory.TaxDecisionBreakdown,
      SecLoansFileCategory.BusinessTaxReturn,
    ],
    [SecLoansCompanyForm.OpenCompany]: [
      SecLoansFileCategory.FinancialStatement,
      SecLoansFileCategory.TaxDecisionBreakdown,
      SecLoansFileCategory.BusinessTaxReturn,
    ],
    [SecLoansCompanyForm.TradeName]: [
      SecLoansFileCategory.TaxDecision,
      SecLoansFileCategory.BusinessTaxReturn,
      SecLoansFileCategory.FinancialStatement,
    ],
  }

  return resolveMapValue(companyFormMap, companyForm) || []
}

const resolveOtherOccupationCategoryMap = (validEmployment: boolean) => {
  return validEmployment ? [SecLoansFileCategory.PaySlip] : []
}

export const getRequiredAttachmentCategories = (
  occupation: SecLoanOccupation | undefined,
  companyForm: SecLoansCompanyForm | undefined,
  validEmployment: boolean,
): SecLoansFileCategory[] => {
  const occupationMap: Record<SecLoanOccupation, SecLoansFileCategory[]> = {
    [Occupation.Employed]: [SecLoansFileCategory.PaySlip],
    [Occupation.Entrepreneur]: resolveEntrepreneurCategoryMap(companyForm),
    [Occupation.Student]: [],
    [Occupation.Pensioner]: [SecLoansFileCategory.PensionCertificate],
    [Occupation.Unemployed]: [],
    [OtherOccupation.OnFamilyLeave]: resolveOtherOccupationCategoryMap(validEmployment),
    [OtherOccupation.Other]: resolveOtherOccupationCategoryMap(validEmployment),
  }
  return resolveMapValue(occupationMap, occupation) || []
}
